import { useSelector } from 'react-redux';
import {
  AssigneeContentWrapper,
  AssigneePopoverLayout,
  AssignmentHistoryBody,
  AssignmentHistoryDate,
  AssignmentHistoryProgress,
  AssingeeHeaderAction,
  AssingeeHeaderContainer,
  HistoryContainer,
  ProgressHistoryStatusInfo,
  PSmallMargin,
} from './styles';
import {
  getAllFieldOptions,
  HiddenFieldValue,
  IAssignmentHistory,
  IField,
  ITask,
  TaskStatus,
} from 'store/tasks/tasksType';
import required from '../../assets/images/required.svg';
import { IUserOption } from 'components/UserSelector/UserSelector';
import { AssigneeHeader, getProgressIcon } from './AssigneeChip';
import { withTitle } from 'components/withTitle';
import {
  black,
  exactWhite,
  green,
  lightBlue,
  lightGray,
  lightRed,
  red,
} from 'utils/style/colors';
import { RootState } from 'store/store';
import { useEffect, useLayoutEffect, useState } from 'react';
import Button from 'components/Button';
import Textarea from 'components/Textarea';
import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';
import Select, { IOption } from 'components/Select/Select';
import { languageDirection } from 'i18n';
import { convertDigitToProperFormat } from 'utils/number';
import { useTranslation } from 'react-i18next';
import { InfoBadgeBlue } from 'components/TaskListView/CircleTaskStatus/styles';
import { smartDateString } from 'utils/date';
import { IProjectStore } from 'store/companies/companiesTypes';
import Icon from 'components/Icon';

interface IProps {
  chip: IUserOption;
  canChangeProgress: boolean;
  onChangeProgress: (
    userId: number,
    progress: number,
    status: TaskStatus,
    progressDescription: string,
    valueOverwrite?: { [key: string]: string }
  ) => void;
  readonlyDescription: string;
  step?: number;
  task: ITask | undefined;
  isAdmin: boolean;
  showPopover?: boolean;
  onSetAssingmentHistory?: (assignmentHistory: IAssignmentHistory[]) => void;
  showFields: boolean | undefined;
  project?: IProjectStore;
  onClosePopover: () => void;
  // onShowPopover: (show: boolean) => void;
}

const AssigneeChipContent = ({
  chip,
  canChangeProgress,
  onChangeProgress,
  readonlyDescription,
  step,
  task,
  isAdmin,
  showPopover,
  onSetAssingmentHistory,
  onClosePopover,
  showFields,
  project,
  // onShowPopover,
}: IProps) => {
  const { t } = useTranslation('taskManager');

  const users = useSelector((state: RootState) => state.users.map);
  const thisUser = useSelector((state: RootState) => state.thisUser);
  const tasks = useSelector((state: RootState) => state.tasks);

  const preventReduceProgress = task?.preventReduceProgress ?? false;

  const [loading, setLoading] = useState<boolean>(false);

  const [assignmentHistoryProgress, setAssignmentHistoryProgress] = useState<
    IAssignmentHistory[]
  >([]);
  const [newAssignmentHistory, setNewAssignmentHistory] = useState<
    Partial<IAssignmentHistory>
  >({});
  const [editAssignmentDateId, setEditAssignmentDateId] = useState<
    number | null
  >(null);
  const [status, setStatus] = useState<IUserOption['status']>('notStarted');
  const [progress, setProgress] = useState<number>(0);
  const [progressDescription, setProgressDescription] = useState<string>('');
  const [valueOverwrite, setValueOverwrite] = useState<{
    [key: string]: string;
  }>({});

  const belongsToChip = (item: IAssignmentHistory) =>
    item.userId === chip.userId;

  const getAssignmentHistoryFromTask = (): IAssignmentHistory[] => {
    if (!task?.assignmentHistoryList) {
      return [];
    }
    return task.assignmentHistoryList
      .filter((item) => belongsToChip(item))
      .sort((a, b) => {
        const aTime =
          a.updateDate instanceof Date
            ? a.updateDate.getTime()
            : Number(a.updateDate);
        const bTime =
          b.updateDate instanceof Date
            ? b.updateDate.getTime()
            : Number(b.updateDate);
        return bTime - aTime;
      });
  };

  useEffect(() => {
    setStatus(chip?.status ?? 'notStarted');
    setProgress(chip?.progress ?? 0);
    setProgressDescription(chip?.progressDescription ?? '');
    setValueOverwrite({});
  }, [task, chip]);

  useLayoutEffect(() => {
    if (showPopover) {
      setAssignmentHistoryProgress(getAssignmentHistoryFromTask());
      setNewAssignmentHistory(generateAssingmentHistory());
      setEditAssignmentDateId(null);
    }
  }, [showPopover, chip.userId, task?.id]);

  const handleClosePopover = () => {
    setEditAssignmentDateId(null);
    setNewAssignmentHistory(generateAssingmentHistory() as IAssignmentHistory);
    onClosePopover?.();
  };

  const hasNewAssignmentHistoryDraft = (
    assignmentHistory?: IAssignmentHistory
  ): boolean => {
    const draft = newAssignmentHistory ?? assignmentHistory;
    return (
      Boolean((draft.progressDescription ?? '').trim()) ||
      Number(draft.progress) !== Number(chip?.progress ?? 0) ||
      draft.status !== (chip?.status ?? 'notStarted')
    );
  };

  const sortAssignmentHistory = (list: IAssignmentHistory[]) =>
    [...list].sort((a, b) => {
      const aTime =
        a.updateDate instanceof Date
          ? a.updateDate.getTime()
          : Number(a.updateDate);
      const bTime =
        b.updateDate instanceof Date
          ? b.updateDate.getTime()
          : Number(b.updateDate);
      return bTime - aTime;
    });

  const commitDraftToAssignmentHistoryProgress = (): IAssignmentHistory[] => {
    if (editAssignmentDateId || !hasNewAssignmentHistoryDraft()) {
      return assignmentHistoryProgress;
    }

    const draftId = newAssignmentHistory.id;
    if (assignmentHistoryProgress.some((item) => item.id === draftId)) {
      return assignmentHistoryProgress;
    }

    return sortAssignmentHistory([
      ...assignmentHistoryProgress,
      {
        ...(newAssignmentHistory as IAssignmentHistory),
        state: 'Add',
        lastEditDate: new Date().getTime(),
      },
    ]);
  };

  const syncAssignmentHistoryToParent = (
    userHistory: IAssignmentHistory[] = assignmentHistoryProgress
  ) => {
    if (!task || !onSetAssingmentHistory) {
      return;
    }

    const otherHistory = (task.assignmentHistoryList ?? []).filter(
      (item) => !belongsToChip(item)
    );
    onSetAssingmentHistory([...otherHistory, ...userHistory]);
  };

  const generateAssingmentHistory = (
    _list?: IAssignmentHistory[]
  ): Partial<IAssignmentHistory> => {
    // New history rows always start as absent/notStarted (progress 0).
    // Do not inherit the assignee's current complete progress — that made
    // "add" commit as complete while the status dropdown showed notStarted.
    return {
      id: Math.random(),
      userId: Number(chip.userId),
      actorId: Number(thisUser.userId),
      taskId: task?.id,
      assignmentId: chip?.assignmentId,
      updateDate: new Date().getTime(),
      status: 'notStarted',
      progress: 0,
      progressDescription: '',
      state: 'Add',
    };
  };

  const generateMinRange = (date: number = new Date().getTime()) => {
    if (!preventReduceProgress) {
      return 0;
    }
    const lastAssignmentHistory = (assignmentHistoryProgress ?? [])
      ?.filter((item: IAssignmentHistory) =>
        item.updateDate instanceof Date
          ? item.updateDate.getTime() < date
          : item.updateDate < date
      )
      ?.at(0) as IAssignmentHistory;

    return lastAssignmentHistory?.progress ?? 0;
  };

  const generateMaxRange = (date: number = new Date().getTime()) => {
    if (!preventReduceProgress) {
      return 100;
    }
    const firstAssignmentHistoryAfterDate = (assignmentHistoryProgress ?? [])
      ?.filter((item: IAssignmentHistory) =>
        item.updateDate instanceof Date
          ? item.updateDate.getTime() > date
          : item.updateDate > date
      )
      ?.at(-1) as IAssignmentHistory;
    return firstAssignmentHistoryAfterDate?.progress ?? 100;
  };

  const handleSaveTask = () => {
    setLoading(true);
    if (!task) {
      return;
    }

    const userHistory = commitDraftToAssignmentHistoryProgress();
    setAssignmentHistoryProgress(userHistory);
    setNewAssignmentHistory(generateAssingmentHistory(userHistory) ?? {});
    setEditAssignmentDateId(null);
    syncAssignmentHistoryToParent(userHistory);
    setLoading(false);
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    e.stopPropagation();
    const value = e.target.value;
    const name = e.target.name;
    if (name === 'progress') {
      setProgress(Number(value));
      if (value === '100') {
        setStatus('complete');
      } else if (value === '0') {
        setStatus('notStarted');
      } else {
        setStatus('started');
      }
    } else if (name === 'status') {
      setStatus(value as IUserOption['status']);
      if (value === 'complete') {
        setProgress(100);
      } else if (value === 'notStarted') {
        setProgress(0);
      } else if (value === 'started') {
        setProgress(1);
      }
    } else if (name === 'progressDescription') {
      setProgressDescription(value);
    }
  };

  const persistAssignmentHistoryToParent = (
    userHistory: IAssignmentHistory[] = assignmentHistoryProgress
  ) => {
    if (!task) {
      return;
    }
    syncAssignmentHistoryToParent(userHistory);
  };

  const handleChangeStatus = (values: IOption[]) => {
    const status = values[0].value as IUserOption['status'];
    setStatus(status);
    if (status === 'complete') {
      setProgress(100);
    } else if (status === 'notStarted') {
      setProgress(0);
    } else if (status === 'started') {
      setProgress(1);
    }
  };

  const handleEditDate = (id: number) => {
    setEditAssignmentDateId(id);
  };

  const handleCancelSaveNewTask = () => {
    setAssignmentHistoryProgress(getAssignmentHistoryFromTask());
    setNewAssignmentHistory(generateAssingmentHistory() as IAssignmentHistory);
    setEditAssignmentDateId(null);
  };

  const handleResetoreSingleProgressHistory = (id: number) => {
    const nextHistory = assignmentHistoryProgress.map((item) =>
      item.id === id ? { ...item, state: 'Exists' as const } : item
    );
    setAssignmentHistoryProgress(nextHistory);
    persistAssignmentHistoryToParent(nextHistory);
  };

  const handleDeleteSingleProgressHistory = () => {
    const nextHistory = assignmentHistoryProgress.map((item) =>
      item.id === editAssignmentDateId
        ? { ...item, state: 'Delete' as const }
        : item
    );
    setAssignmentHistoryProgress(nextHistory);
    persistAssignmentHistoryToParent(nextHistory);
  };

  const handleDeleteNewProgressHistory = () => {
    const nextHistory = assignmentHistoryProgress.filter(
      (item) => item.id !== editAssignmentDateId
    );
    setAssignmentHistoryProgress(nextHistory);
    setEditAssignmentDateId(null);
    setNewAssignmentHistory(generateAssingmentHistory() as IAssignmentHistory);
    persistAssignmentHistoryToParent(nextHistory);
  };

  const handleChangeFieldValues = (name: string, values: IOption[]) => {
    const newValue = values.map((item) => item.value).join(',');
    setValueOverwrite({ ...valueOverwrite, [name]: newValue });
  };

  const handleChangeFieldValue = (name: string, value: string) => {
    setValueOverwrite({ ...valueOverwrite, [name]: value });
  };

  const handleDateFieldChangeEvent = (
    name: string,
    value: string | number | null
  ) => {
    setValueOverwrite({
      ...valueOverwrite,
      [name]: value
        ? (Number(value) + 12 * 60 * 60 * 1000).toString()
        : (value as string),
    });
  };

  // const updateOrAddAssignmentHistoryProgress = (
  //   assignment: IAssignmentHistory
  // ) => {
  //   const assignmentHistory = assignmentHistoryProgress.find(
  //     (ah: IAssignmentHistory) => ah.id === assignment.id
  //   );
  //   if (assignmentHistory) {
  //     assignmentHistory.state = 'Update';
  //     setAssignmentHistoryProgress(
  //       assignmentHistoryProgress.map((ah) =>
  //         ah.id === assignment.id ? assignment : ah
  //       )
  //     );
  //     persistAssignmentHistoryToParent();
  //   } else {
  //     setAssignmentHistoryProgress(
  //       [
  //         ...assignmentHistoryProgress,
  //         { ...assignment, id: Math.random() },
  //       ].sort((a, b) => (b.updateDate > a.updateDate ? 1 : -1))
  //     );
  //     persistAssignmentHistoryToParent();
  //   }
  // };

  const handleChangeAssignmentHistory = (
    assignmentId: number,
    field: 'status' | 'progress' | 'progressDescription' | 'updateDate',
    value: any
  ) => {
    if (assignmentId === newAssignmentHistory.id) {
      setNewAssignmentHistory({
        ...newAssignmentHistory,
        [field]: value,

        ...(field === 'status'
          ? {
              progress:
                value === 'complete'
                  ? 100
                  : value === 'notStarted'
                    ? 0
                    : value === 'started'
                      ? 1
                      : newAssignmentHistory?.progress,
            }
          : {}),

        ...(field === 'progress'
          ? {
              status:
                Number(value) === 0
                  ? 'notStarted'
                  : Number(value) === 100
                    ? 'complete'
                    : 'started',
            }
          : {}),
      });
    } else {
      const nextHistory: IAssignmentHistory[] = assignmentHistoryProgress.map(
        (ah: IAssignmentHistory): IAssignmentHistory =>
          ah.id === assignmentId
            ? {
                ...ah,
                [field]: value,
                lastEditDate: new Date().getTime(),
                state: (ah.state === 'Add' ? 'Add' : 'Update') as
                  | 'Add'
                  | 'Update'
                  | 'Delete'
                  | 'Exists',
                ...(field === 'status'
                  ? {
                      progress:
                        value === 'complete'
                          ? 100
                          : value === 'notStarted'
                            ? 0
                            : ah.progress,
                    }
                  : {}),
                ...(field === 'progress'
                  ? {
                      status: (Number(value) === 0
                        ? 'notStarted'
                        : Number(value) === 100
                          ? 'complete'
                          : 'started') as TaskStatus,
                    }
                  : {}),
              }
            : ah
      );
      setAssignmentHistoryProgress(nextHistory);
      persistAssignmentHistoryToParent(nextHistory);
    }
  };

  const getAssignmentHistoryItem = (assingmentId: number) => {
    if (assingmentId === newAssignmentHistory.id) {
      return newAssignmentHistory;
    } else {
      return assignmentHistoryProgress.find(
        (ah: IAssignmentHistory) => ah.id === assingmentId
      );
    }
  };

  const toTimestamp = (date: Date | string | number): number => {
    if (date instanceof Date) return date.getTime();
    return Number(date);
  };

  const getMinStartDate = (task: ITask): number | null => {
    const history = [...(task.assignmentHistoryList || [])].sort(
      (a, b) => toTimestamp(a.updateDate) - toTimestamp(b.updateDate)
    );

    for (const entry of history) {
      if (entry.progress > 0) {
        return toTimestamp(entry.updateDate);
      }
    }
    return null;
  };

  const getMaxEndDate = (task: ITask): number | null => {
    const history = [...(task.assignmentHistoryList || [])].sort(
      (a, b) => toTimestamp(a.updateDate) - toTimestamp(b.updateDate)
    );

    for (const entry of history) {
      if (entry.progress >= 100) {
        return toTimestamp(entry.updateDate);
      }
    }
    return null;
  };

  const getValidMinDate = (task: ITask): number | null => {
    if (!task.relatedTasks?.length) return null;

    let validMinDate = Number.NEGATIVE_INFINITY;

    for (const rt of task.relatedTasks) {
      const relatedTask = tasks?.data?.[rt.taskId] as ITask | undefined;
      if (!relatedTask) continue;

      let relatedDate: number | null = null;

      if (rt.properties.dependency === 'RequireStart') {
        relatedDate = getMinStartDate(relatedTask);
      } else if (rt.properties.dependency === 'RequireFinish') {
        relatedDate = getMaxEndDate(relatedTask);
      }

      if (relatedDate !== null) {
        validMinDate = Math.max(validMinDate, relatedDate);
      }
    }

    return validMinDate === Number.NEGATIVE_INFINITY ? null : validMinDate;
  };

  const handleRestoreEditedSingleProgressHistory = () => {
    if (task?.id) {
      const singleAssignmentHistory = (
        tasks?.data?.[task?.id]?.assignmentHistoryList ?? []
      ).find((el) => el.id === editAssignmentDateId);

      let newAssignmentsHistory: IAssignmentHistory[] = [];
      if (singleAssignmentHistory) {
        newAssignmentsHistory = assignmentHistoryProgress.map((el) =>
          el.id === singleAssignmentHistory.id ? singleAssignmentHistory : el
        );
      } else {
        newAssignmentsHistory = assignmentHistoryProgress.filter(
          (el) => el.id !== editAssignmentDateId
        );
      }
      setAssignmentHistoryProgress(newAssignmentsHistory);
      onSetAssingmentHistory?.(newAssignmentsHistory);
    }
  };

  const handleResetProgressHistory = () => {
    if (task) {
      const taskProgressHistory =
        tasks?.data?.[task.id]?.assignmentHistoryList
          .filter((el) => belongsToChip(el))
          .sort(
            (a, b) => toTimestamp(b.updateDate) - toTimestamp(a.updateDate)
          ) ?? [];

      setAssignmentHistoryProgress(taskProgressHistory);
      setNewAssignmentHistory(
        generateAssingmentHistory() as IAssignmentHistory
      );
      setEditAssignmentDateId(null);
    }
  };

  const handleAddNewProgressHistory = () => {
    setEditAssignmentDateId(null);
    setNewAssignmentHistory(generateAssingmentHistory() as IAssignmentHistory);
  };

  const onSave = () => {
    onChangeProgress?.(
      chip.userId,
      progress,
      status as TaskStatus,
      progressDescription,
      valueOverwrite
    );

    onClosePopover?.();
  };

  const getProgressHistoryComponent = (
    statusOptions: IOption[],
    assignmentId: number | null
  ) => {
    const taskObject = task;

    const enableChangeHistory = assignmentId ? true : false;

    const assignmentHistory = assignmentId
      ? getAssignmentHistoryItem(assignmentId)
      : undefined;

    const assignmentProgress = enableChangeHistory
      ? assignmentHistory?.progress
      : progress;
    const assignmentStatus = enableChangeHistory
      ? assignmentHistory?.status
      : status;
    const assignmentProgressDescription = enableChangeHistory
      ? assignmentHistory?.progressDescription
      : progressDescription;
    const assignmentUpdateDate = enableChangeHistory
      ? assignmentHistory?.updateDate
      : undefined;

    const isDeletedOrDisabled = enableChangeHistory
      ? assignmentHistory?.state === 'Delete' ||
        (editAssignmentDateId
          ? Number(thisUser.userId) !== assignmentHistory?.actorId
          : false)
      : false;

    const userOnlyCanDelete =
      enableChangeHistory && Boolean(editAssignmentDateId)
        ? Number(thisUser.userId) !== assignmentHistory?.actorId
        : false;

    const showWarningOfCanNotAddProgressHistory = enableChangeHistory
      ? assignmentHistory?.state === 'Add' &&
        !hasNewAssignmentHistoryDraft(assignmentHistory as IAssignmentHistory)
      : false;

    const statusOptionsFilteredByScoreRange = enableChangeHistory
      ? statusOptions.filter((item) => {
          const minRange = generateMinRange(assignmentUpdateDate as number);
          const maxRange = generateMaxRange(assignmentUpdateDate as number);
          return item.value === 'complete'
            ? maxRange === 100
            : item.value === 'notStarted'
              ? minRange === 0
              : true;
        })
      : statusOptions;

    return (
      <>
        <Select
          multiChoice={false}
          options={statusOptionsFilteredByScoreRange}
          values={statusOptionsFilteredByScoreRange.filter(
            (item) => item.value === assignmentStatus
          )}
          onChangeValues={(values) =>
            enableChangeHistory
              ? handleChangeAssignmentHistory(
                  assignmentId!,
                  'status',
                  values[0].value
                )
              : handleChangeStatus(values)
          }
          allowRemove={false}
          disabled={isDeletedOrDisabled}
        />
        {task && task.type === 'progressive' && (
          <>
            <PSmallMargin>
              {t('assigneeProgress')}:{' '}
              {languageDirection() === 'rtl'
                ? convertDigitToProperFormat(
                    assignmentProgress?.toString() ?? '0'
                  ) + t('percent')
                : `${assignmentProgress} ${t('percent')}`}
            </PSmallMargin>
            {!userOnlyCanDelete && (
              <PSmallMargin className="range-field">
                <input
                  type="range"
                  min={generateMinRange(assignmentUpdateDate as number)}
                  max={generateMaxRange(assignmentUpdateDate as number)}
                  step={step ?? 1}
                  name="progress"
                  id={chip.userId.toString()}
                  value={assignmentProgress}
                  onChange={(e: any) =>
                    enableChangeHistory
                      ? handleChangeAssignmentHistory(
                          assignmentId!,
                          'progress',
                          Number(e.target.value)
                        )
                      : handleChangeInput(e)
                  }
                  disabled={isDeletedOrDisabled}
                />
              </PSmallMargin>
            )}
            {enableChangeHistory && !userOnlyCanDelete && (
              <PSmallMargin>
                <MultiDatePicker
                  id={'HistoryUpdateDate'}
                  setDate={(value) => {
                    const currentTime = new Date().getTime();
                    if (value && enableChangeHistory) {
                      handleChangeAssignmentHistory(
                        assignmentId!,
                        'updateDate',
                        value
                          ? value > currentTime
                            ? currentTime
                            : value
                          : new Date().getTime()
                      );
                    }
                  }}
                  placeholder={
                    t('assignmentHistoryUpdateDate', {
                      ns: 'taskManager',
                    }) as string
                  }
                  label={
                    t('assignmentHistoryUpdateDate', {
                      ns: 'taskManager',
                    }) as string
                  }
                  value={(assignmentUpdateDate as number) ?? undefined}
                  hasTime={true}
                  minDate={
                    isAdmin
                      ? undefined
                      : Math.max(
                          new Date().getTime() -
                            (task?.validUpdateDurationInMinutes ?? 0) *
                              60 *
                              1000,
                          getValidMinDate(task) ?? 0,
                          task?.startDate ?? 0
                        )
                  }
                  maxDate={new Date().getTime()}
                  isEditable={!isDeletedOrDisabled}
                />
              </PSmallMargin>
            )}
          </>
        )}
        <PSmallMargin>
          <Textarea
            id={'progressDescription' + chip.userId.toString()}
            name="progressDescription"
            value={assignmentProgressDescription}
            onChange={(e: any) =>
              enableChangeHistory
                ? handleChangeAssignmentHistory(
                    assignmentId!,
                    'progressDescription',
                    e.target.value
                  )
                : handleChangeInput(e)
            }
            label={t('progressDescription')}
            disabled={isDeletedOrDisabled}
          />
        </PSmallMargin>
        {isDeletedOrDisabled && userOnlyCanDelete && (
          <div
            style={{
              color: exactWhite,
              background: lightRed,
              borderRadius: '0.5rem',
              padding: '0.5rem 0.25rem',
              margin: '0.5rem 0',
            }}
          >
            {t('onlyCanDelete')}
          </div>
        )}
        {showWarningOfCanNotAddProgressHistory && !editAssignmentDateId ? (
          <InfoBadgeBlue>{t('canNotAddProgressHistory')}</InfoBadgeBlue>
        ) : (
          <></>
        )}
        <div>
          {showFields && taskObject ? (
            taskObject.fields
              .filter(
                (it: any) =>
                  it.relatedToProgress && it.value !== HiddenFieldValue
              )
              .map((field: IField, index: number) => {
                const allFieldOptions = getAllFieldOptions(
                  taskObject,
                  field,
                  tasks,
                  project
                );
                const currentValue = (
                  field.name in valueOverwrite
                    ? valueOverwrite[field.name]
                    : field.value
                ) as string | undefined;
                return (
                  <div key={index} className="row">
                    <label
                      className="col s12 m12"
                      style={{
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      htmlFor={field.name}
                    >
                      {field.required && (
                        <img
                          src={required}
                          className={
                            !currentValue || /^\s*$/.test(currentValue)
                              ? 'filter-red'
                              : ''
                          }
                          style={{
                            padding: '7px',
                          }}
                        />
                      )}
                      <span>{field.title}</span>
                    </label>
                    {field.options && allFieldOptions.length > 0 ? (
                      <Select
                        values={(field.type === 'multiSelect'
                          ? (currentValue || '').split(',')
                          : [currentValue || '']
                        ).reduce((current: IOption[], item) => {
                          if (item)
                            current.push({
                              label: item,
                              value: item,
                            });
                          return current;
                        }, [] as IOption[])}
                        options={allFieldOptions
                          .sort((a, b) => {
                            if (a < b) {
                              return -1;
                            }
                            if (a > b) {
                              return 1;
                            }
                            return 0;
                          })
                          .reduce((current: IOption[], item) => {
                            current.push({
                              label: item,
                              value: item,
                            });
                            return current;
                          }, [] as IOption[])}
                        onChangeValues={(values) =>
                          handleChangeFieldValues(field.name, values)
                        }
                        multiChoice={field.type === 'multiSelect'}
                      />
                    ) : field.type === 'date' ? (
                      <MultiDatePicker
                        className="col s12 m12 roundDateInputInTask"
                        id={field.name}
                        setDate={(value) =>
                          handleDateFieldChangeEvent(index.toString(), value)
                        }
                        value={Number(currentValue) ?? null}
                      />
                    ) : (
                      <Textarea
                        className="col s12 m12"
                        key={index}
                        id={field.name}
                        name={field.name}
                        value={currentValue || ''}
                        onChange={(event) =>
                          handleChangeFieldValue(
                            event.target.name,
                            event.target.value
                          )
                        }
                        // label={field.title}
                        placeholder={field.title}
                        style={{
                          minHeight: '2.5rem',
                          height: '2.5rem',
                        }}
                      />
                    )}
                  </div>
                );
              })
          ) : (
            <div />
          )}
        </div>
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          {showFields ? (
            <Button
              color="green"
              onClick={onSave}
              disabled={Boolean(
                taskObject?.fields?.find(
                  (it) =>
                    it.required &&
                    it.name in valueOverwrite &&
                    !valueOverwrite[it.name]
                )
              )}
              loading={loading}
            >
              {t('confirm')}
            </Button>
          ) : editAssignmentDateId &&
            assignmentHistoryProgress?.find(
              (item) => item.id === editAssignmentDateId
            )?.state === 'Add' ? (
            <>
              <Button
                color="red-outline"
                onClick={handleDeleteNewProgressHistory}
              >
                <Icon
                  type="trash"
                  color={exactWhite}
                  style={{ padding: 'none' }}
                />
              </Button>
            </>
          ) : editAssignmentDateId ? (
            <>
              {assignmentHistoryProgress.find(
                (item) => item.id === editAssignmentDateId
              )?.state === 'Delete' ? (
                <Button
                  color="green-outline"
                  onClick={() =>
                    handleResetoreSingleProgressHistory(editAssignmentDateId!)
                  }
                >
                  <div className="flex gap-1">
                    <div>{t('restore')}</div>
                    <Icon
                      type="rotate-ccw"
                      color="red"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </Button>
              ) : (
                <>
                  <Button
                    color="green-outline"
                    onClick={handleRestoreEditedSingleProgressHistory}
                    disabled={isDeletedOrDisabled}
                  >
                    {t('restoreProgressHistory')}
                  </Button>
                  <Button
                    color="red-outline"
                    onClick={handleDeleteSingleProgressHistory}
                  >
                    <Icon
                      type="trash"
                      color={exactWhite}
                      style={{ padding: 'none' }}
                    />
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <Button
                color="green-outline"
                onClick={handleSaveTask}
                disabled={Boolean(
                  taskObject?.fields?.find(
                    (it) =>
                      it.required &&
                      it.name in valueOverwrite &&
                      !valueOverwrite[it.name]
                  ) || showWarningOfCanNotAddProgressHistory
                )}
                loading={loading}
              >
                {t('createNewProgressHistory')}
              </Button>

              <Button
                color="red-outline"
                onClick={handleCancelSaveNewTask}
                disabled={loading}
              >
                <Icon
                  type="trash"
                  color={exactWhite}
                  style={{ padding: 'none' }}
                />
              </Button>
            </>
          )}
        </div>
      </>
    );
  };

  const canUserChangeProgress =
    thisUser && isAdmin
      ? true
      : task?.validUpdateDurationInMinutes
        ? task.validUpdateDurationInMinutes > 0
        : false;

  const allPrerequisitsFulfilled = canUserChangeProgress
    ? task?.relatedTasks?.every((rt) =>
        rt.properties.dependency === 'RequireStart'
          ? (tasks?.data?.[rt.taskId]?.mainProgress ?? 0 > 0)
          : rt.properties.dependency === 'RequireFinish'
            ? tasks?.data?.[rt.taskId]?.mainProgress === 100
            : true
      )
    : false;

  const statusOptions: IOption[] = [
    {
      label: (task && task.assigneeCompleteTitle) ?? t('complete'),
      value: 'complete',
    },
    {
      label: (task && task.assigneeStartedTitle) ?? t('started'),
      value: 'started',
    },
    {
      label: (task && task.assigneeNotStartedTitle) ?? t('notStarted'),
      value: 'notStarted',
    },
  ];

  const isResetEnabled = assignmentHistoryProgress?.find((item) =>
    ['Add', 'Update', 'Delete'].includes(item.state)
  );

  return (
    <div
      style={{ display: 'flex', padding: '10px', flexDirection: 'column' }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <AssingeeHeaderContainer>
        <AssigneeHeader htmlFor="status">
          {chip.name}
          {chip.relatedRoleName && ' (' + chip.relatedRoleName + ')'}
        </AssigneeHeader>
        <AssingeeHeaderAction>
          {editAssignmentDateId ? (
            <div>
              {withTitle(
                <Icon
                  type="plus-circle"
                  color={green}
                  onClick={() => handleAddNewProgressHistory()}
                  style={{ cursor: 'pointer' }}
                />,
                t('addNewProgressHistory', { ns: 'taskManager' })
              )}
            </div>
          ) : (
            <> </>
          )}
          <div>
            {isResetEnabled &&
              canUserChangeProgress &&
              withTitle(
                <Icon
                  type="rotate-ccw"
                  color="red"
                  onClick={() => handleResetProgressHistory()}
                  style={{ cursor: 'pointer' }}
                />,
                t('restoreAllProgressHistory', { ns: 'taskManager' })
              )}
          </div>
          <div>
            <Icon
              type="x-circle"
              color="red"
              onClick={() => handleClosePopover()}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </AssingeeHeaderAction>
      </AssingeeHeaderContainer>
      <AssigneePopoverLayout>
        <AssigneeContentWrapper>
          {canChangeProgress && !showFields && !editAssignmentDateId ? (
            <div>
              {getProgressHistoryComponent(
                statusOptions,
                newAssignmentHistory.id as number
              )}
            </div>
          ) : canChangeProgress && editAssignmentDateId ? (
            <div>
              {getProgressHistoryComponent(statusOptions, editAssignmentDateId)}
            </div>
          ) : canChangeProgress ? (
            <>{getProgressHistoryComponent(statusOptions, null)}</>
          ) : (
            <div>
              {readonlyDescription || (
                <InfoBadgeBlue>{t('canNotEditProgress')}</InfoBadgeBlue>
              )}{' '}
            </div>
          )}
        </AssigneeContentWrapper>
        {task && assignmentHistoryProgress?.length ? (
          <HistoryContainer>
            {assignmentHistoryProgress?.map((item: IAssignmentHistory) => {
              return (
                <div
                  key={`${item.assignmentId} ${item.updateDate}`}
                  style={{
                    backgroundColor: editAssignmentDateId
                      ? item.id === editAssignmentDateId
                        ? lightGray
                        : 'transparent'
                      : 'transparent',
                  }}
                >
                  <div className="flex gap-1 items-center">
                    <AssignmentHistoryProgress>
                      {' '}
                      {task.type === 'progressive' &&
                        convertDigitToProperFormat(
                          item.progress.toString() + t('percent')
                        )}
                      {task.type === 'atOnce' && (
                        <Icon type={getProgressIcon(item.progress)} />
                      )}
                    </AssignmentHistoryProgress>
                    <AssignmentHistoryDate>
                      <div>
                        {withTitle(
                          <div>{smartDateString(item.updateDate, true)}</div>,
                          t('lastEditDateInfo', {
                            ns: 'taskManager',
                            date: smartDateString(
                              item.lastEditDate ?? item.updateDate,
                              true
                            ),
                          }) as string
                        )}
                      </div>
                      {['Update', 'Add', 'Delete'].includes(item?.state) && (
                        <ProgressHistoryStatusInfo>
                          {item?.state === 'Add' ? (
                            <div style={{ color: green }}>
                              {
                                t('created', {
                                  ns: 'taskManager',
                                }) as string
                              }
                            </div>
                          ) : item?.state === 'Update' ? (
                            <div style={{ color: lightBlue }}>
                              {
                                t('updated', {
                                  ns: 'taskManager',
                                }) as string
                              }
                            </div>
                          ) : item?.state === 'Delete' ? (
                            <div style={{ color: red }}>
                              {
                                t('deleted', {
                                  ns: 'taskManager',
                                }) as string
                              }
                            </div>
                          ) : (
                            <></>
                          )}
                        </ProgressHistoryStatusInfo>
                      )}
                    </AssignmentHistoryDate>

                    {(thisUser &&
                      Number(thisUser.userId) === item.actorId &&
                      canChangeProgress) ||
                    isAdmin ? (
                      <Icon
                        onClick={() => {
                          if (
                            allPrerequisitsFulfilled ||
                            item?.actorId === Number(thisUser?.userId)
                          )
                            handleEditDate(item.id);
                        }}
                        type="edit"
                        style={{
                          marginInlineStart: 'auto',
                          display: 'flex',
                          padding: '3px',
                          cursor: 'pointer',
                          color:
                            !allPrerequisitsFulfilled ||
                            (item?.actorId !== Number(thisUser?.userId) &&
                              !isAdmin)
                              ? lightGray
                              : black,
                        }}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                  <div
                    style={{
                      borderInlineStart: `1px solid ${green}`,
                      paddingInlineStart: '5px',
                      marginInlineStart: '23px',
                      maxWidth: '20rem !important',
                      width: '20rem !important',
                    }}
                  >
                    {/*<td>*/}
                    <AssignmentHistoryBody>
                      {users[item.actorId!]?.name}
                      {item.progressDescription
                        ? ' : ' + item.progressDescription
                        : ''}
                    </AssignmentHistoryBody>
                    {/*</td>*/}
                  </div>
                </div>
              );
            })}
          </HistoryContainer>
        ) : (
          <></>
        )}
      </AssigneePopoverLayout>
    </div>
  );
};

export default AssigneeChipContent;
